import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { Distance } from '@/types/Distance';
import { ItemCardInfo } from '@/components/GridItemCard/types/types';
import { LotRecommendationType } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { PAGE_ITEMS_TO_VIEW } from '@/components/Pagination/helpers/filterSortOptions';
import { SearchPageTabs } from '../SearchPageTabs/SearchPageTabs';
import { SearchPageZeroResultsBanner } from './SearchPageZeroResultsBanner';
import { SearchParams } from '@/types/SaveSearch';
import { useImageUrls } from './hooks/useImagesUrls';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useMemo } from 'react';
import { useSimilarActiveItems } from './hooks/useSimilarActiveItems';
import CategorySearchCard from '@/components/GridItemCard/Card/CardOptions/CategorySearchCards/CategorySearchCard';
import ItemCard from '@/components/GridItemCard/Card/Card';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';

const DESKTOP_CARD_DIMENSION = 246;
const MOBILE_CARD_DIMENSION = 164;

type SearchPageZeroResultsProps = {
    canonicalURL: string;
    distance: Distance;
    hasNoFilterTermOrRangeResults: boolean;
    isArchivedSearch: boolean;
    isSEOSearchPage: boolean;
    onChange: ({ page, pageSize }: { page: number; pageSize: number }) => void;
    page: number;
    pageSize: number;
    saveSearchParams: SearchParams;
    searchTerm: string;
    totalFound: number;
    totalPages: number;
    totalSoldItems: number;
};

export const SearchPageZeroResults = ({
    canonicalURL,
    distance,
    hasNoFilterTermOrRangeResults,
    isArchivedSearch,
    isSEOSearchPage,
    onChange,
    page,
    pageSize,
    saveSearchParams,
    searchTerm,
    totalFound,
    totalPages,
    totalSoldItems,
}: SearchPageZeroResultsProps) => {
    const { formatMessage } = useIntl();
    const imageUrls = useImageUrls();
    const similarActiveItemIds = useSimilarActiveItems();
    const isMobileLayout = useIsMobileLayout();

    const similarActiveCards = useMemo(() => {
        const currentDimension = isMobileLayout ? MOBILE_CARD_DIMENSION : DESKTOP_CARD_DIMENSION;

        const itemCardInfo: ItemCardInfo = {
            dimensions: { desktop: currentDimension, mobile: currentDimension },
            ItemCardComponent: CategorySearchCard.ItemCardComponent,
        };

        return similarActiveItemIds.slice(0, 12).map((itemId, index) => ({
            card: (
                <ItemCard
                    itemCardOption={itemCardInfo}
                    itemId={itemId}
                    key={`similar-active-${itemId}`}
                    recommendationPosition={index + 1}
                    recommendationType={LotRecommendationType.YouMayAlsoLike}
                />
            ),
            key: `similar-active-${itemId}`,
        }));
    }, [similarActiveItemIds, isMobileLayout]);

    return (
        <>
            <SearchPageZeroResultsBanner
                imageUrls={imageUrls}
                saveSearchParams={saveSearchParams}
                searchTerm={searchTerm}
                totalSoldItems={totalSoldItems}
            />
            <div className="mb-2xl desktop:min-h-[469px] smMax:mb-xl">
                <Carousel
                    cards={similarActiveCards}
                    title="You May Also Like"
                />
            </div>
            <div>
                <H2 className="mb-2xs">
                    {formatMessage(
                        { id: 'searchPage.zeroResults.pastResultsTitle' },
                        {
                            plural: totalSoldItems > 1 ? 's' : '',
                            searchTerm,
                            totalSoldItems,
                        }
                    )}
                </H2>
                <BodyPrimary className="text-text-secondary">
                    {formatMessage({ id: 'searchPage.zeroResults.pastResultsBody' })}
                </BodyPrimary>
            </div>
            <Pagination
                canonicalUrl={canonicalURL}
                onChange={onChange}
                pageSizeOptions={PAGE_ITEMS_TO_VIEW}
                paginationFilter={{ page, pageSize, totalRecords: totalFound }}
                totalPages={totalPages}
            >
                <SearchPageTabs
                    distance={distance}
                    hasNoFilterTermOrRangeResults={hasNoFilterTermOrRangeResults}
                    hideTabNavigation
                    isArchivedSearch={isArchivedSearch}
                    isSEOSearchPage={isSEOSearchPage}
                    searchTerm={searchTerm}
                    totalSoldItems={totalSoldItems}
                />
            </Pagination>
        </>
    );
};
